import { BundleProductTypes, ProductKind, ProductSegment } from 'constants/product'
import { InstructionType, editingProductKindSortingOrder, resolveProductGroupKind } from 'constants/purchaseFlow'
import { CatalogueOptionProduct, CatalogueProduct, PurchaseProductCatalogue, useGetAddressConfig$, useGetCategoryConfig$, useGetExtraProducts$, useGetInstructionConfig$, useGetProducts$ } from 'dataQueries/purchase.query'
import { InstructionOptionDTO, Place } from 'models/purchaseFlow'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GooglePlaceComponent, getFromGooglePlace } from 'utils/location/googlePlaceUtils'

import Big from 'big.js'
import constate from 'constate'
import { Nullable } from 'models/helpers'
import { Path } from 'constants/router'
import { ProductCategory } from 'models/product'
import { ProductKindWithProducts, ProductKindsPerSegmentCollection, ProductTypeWithProducts, ProductTypesPerSegmentCollection, collapseSegmentsToAll, isSegmentationExemptCategory } from './catalogueSegments'
import { bigFromDecimal } from 'utils/price'
import { isShootingCategory } from 'utils/validators'
import { useMatch } from 'react-router-dom'
import { useOrderAttempt } from 'utils/analytics/useOrderAttempt'
import { useOrderFlowPropertyTracking } from '../hooks/useOrderFlowPropertyTracking'
import { usePurchaseFlowRealEstateProperty } from './PurchaseFlowRealEstateProperty.context'

export const [PurchaseFlowConfigProvider, usePurchaseFlowConfig] = constate(() => {

  const { selectedAssignmentPlace, setSelectedAssignmentPlace, selectedProperty, isUpdatePropertyAddressFlow, realEstatePropertyId, isPropertyPrefilled } = usePurchaseFlowRealEstateProperty()

  // useMatch (not useEmbedConfig) so this context does not depend on the EmbedConfig provider being
  // an ancestor: it mirrors EmbedConfig's own detection, keeping the snapshot/affordance embed-gating
  // self-contained. Everything below is inert outside the embed route.
  const isEmbed = useMatch(Path.EMBED_PURCHASE_FLOW) !== null

  // One attempt per purchase-flow mount; a mount no click started is Propstack opening the embed, or a direct entry
  const { orderAttemptId, launchedFrom } = useOrderAttempt(isEmbed ? 'crm_order_card' : 'direct')
  // The property the attempt's events are about (ISSUE-3932). Here, not in the property context: NewProperty reuses that.
  // isPropertyPrefilled stays null while the URL's property is being looked up.
  useOrderFlowPropertyTracking(realEstatePropertyId, isPropertyPrefilled === null)

  // Property DTO is authoritative for pricing (its geocode is async/can fail, which would leave the catalogue empty in embed); geocode wins only for manual/update-address entry, 'XX' country = unusable
  const authoritativePropertyAddress = useMemo(() => {
    if (isUpdatePropertyAddressFlow) return null

    const address = selectedProperty?.address
    if (!address?.city || !address.country || address.country === 'XX') return null

    return address
  }, [isUpdatePropertyAddressFlow, selectedProperty?.address])

  // CATEGORY CONFIG
  const getCategoryConfig = useGetCategoryConfig$()

  const [selectedCategory, setSelectedCategory] = useState<ProductCategory | undefined>(undefined)
  const [enableSkipCtAutomation, setEnableSkipCtAutomation] = useState<boolean>(false)
  const [enableSkipExternalProviderSending, setEnableSkipExternalProviderSending] = useState<boolean>(false)

  // Overrides from recalculate-prices endpoint (invoicing address VAT recalculation)
  const [vatRateOverride, setVatRateOverride] = useState<Big | null>(null)
  const [discountOverride, setDiscountOverride] = useState<Big | null>(null)

  const computedCategoryData = useMemo(
    () => ({
      sessionId: getCategoryConfig.data?.data.sessionId ?? null,
      skipCategoryStep: getCategoryConfig.data?.data.data.visible !== undefined ? !getCategoryConfig.data?.data.data.visible : false,
      defaultCategory: getCategoryConfig.data?.data.data.defaultCategory ?? null,
      shootingCategories: getCategoryConfig.data?.data.data.shootingCategories ?? [],
      editingCategories: getCategoryConfig.data?.data.data.editingCategories ?? [],
      documentCategories: getCategoryConfig.data?.data.data.documentCategories ?? [],
      fallbackCountryCode: getCategoryConfig.data?.data.data.countryCode,
    }),
    [getCategoryConfig.data?.data.sessionId, getCategoryConfig.data?.data.data]
  )

  // ADDRESS CONFIG
  const getAddressConfig = useGetAddressConfig$()

  const computedAddressData = useMemo(() => {
    let defaultAddressPlace: Place | null = null

    if (
      getAddressConfig.data?.data.data.defaultAddress
      && getAddressConfig.data?.data.data.defaultCoordinates
      && getAddressConfig.data?.data.data.defaultCountryCode
    ) {
      defaultAddressPlace = {
        address: getAddressConfig.data.data.data.defaultAddress,
        countryCode: getAddressConfig.data.data.data.defaultCountryCode,
        coordinate: getAddressConfig.data.data.data.defaultCoordinates,
      }
    }

    return {
      defaultAddressPlace,
      skipAddressStep: getAddressConfig.data?.data.data.visible !== undefined ? !getAddressConfig.data?.data.data.visible : false,
      selectedCity: authoritativePropertyAddress ? authoritativePropertyAddress.city : getFromGooglePlace(selectedAssignmentPlace, GooglePlaceComponent.city),
      selectedCountryCode: authoritativePropertyAddress ? authoritativePropertyAddress.country : getFromGooglePlace(selectedAssignmentPlace, GooglePlaceComponent.country),
      regionCode: authoritativePropertyAddress ? authoritativePropertyAddress.regionCode : getFromGooglePlace(selectedAssignmentPlace, GooglePlaceComponent.regionCode),
      postalCode: authoritativePropertyAddress ? authoritativePropertyAddress.postalCode : getFromGooglePlace(selectedAssignmentPlace, GooglePlaceComponent.postalCode),
    }
  }, [getAddressConfig?.data?.data.data, selectedAssignmentPlace, authoritativePropertyAddress])

  // PRODUCT CONFIG
  const getProductCatalogue = useGetProducts$()

  // The product catalogue is fetched via a MUTATION (live OneSystem repricing re-fires it on every
  // selection change). In react-query v5, calling mutate() again resets the mutation state, so
  // `data` becomes undefined while the new call is pending — which would blank the whole catalogue-derived
  // UI (segments, listing, prices) and flicker back ~0.5-1s later. To kill that flicker in the embed we
  // keep the last SUCCESSFUL catalogue in a ref and read from it while a reprice is in flight.
  // A category/address change legitimately empties the catalogue, so the invalidation effect below clears
  // the snapshot to avoid carrying stale products across a real change. Outside the embed this fallback is
  // inert (the ref is only read when isEmbed), so non-embed behaviour is byte-identical.
  const lastSuccessfulCatalogueRef = useRef<PurchaseProductCatalogue | null>(null)
  if (getProductCatalogue.isSuccess && getProductCatalogue.data?.data.data) {
    lastSuccessfulCatalogueRef.current = getProductCatalogue.data.data.data
  }

  // The catalogue payload the UI actually renders: live data when present, else (embed only) the last
  // successful snapshot so mid-reprice `data === undefined` never blanks the listing.
  const effectiveCatalogue: PurchaseProductCatalogue | undefined =
    getProductCatalogue.data?.data.data ?? (isEmbed ? lastSuccessfulCatalogueRef.current ?? undefined : undefined)

  // A reprice is in flight (embed only): the mutation is pending AND a snapshot exists, i.e. this is NOT
  // the very first catalogue load (which has no snapshot to fall back on and shows the normal loading state).
  const isRepricing = isEmbed && getProductCatalogue.isPending && lastSuccessfulCatalogueRef.current !== null

  // Separate VAT/discount from product catalogue computation to avoid re-triggering
  // product data recalculation (and downstream segment resets) when only overrides change
  const catalogueDiscount = useMemo(
    () => discountOverride ?? bigFromDecimal(effectiveCatalogue?.decimalDiscount.value || 0),
    [discountOverride, effectiveCatalogue?.decimalDiscount.value]
  )
  const catalogueVat = useMemo(
    () => vatRateOverride ?? bigFromDecimal(effectiveCatalogue?.decimalVat.value || 0),
    [vatRateOverride, effectiveCatalogue?.decimalVat.value]
  )

  // It's a bit expensive, but is triggered only once per unique product config fetch
  const computedProductData = useMemo(() => {
    const availableSegments: Array<ProductSegment> = []
    const availableProductTypesPerSegment: ProductTypesPerSegmentCollection = {}
    const availableProductKindsPerSegment: ProductKindsPerSegmentCollection = {}
    const allProducts: Array<CatalogueProduct> = []

    const catalogueCurrency = effectiveCatalogue?.currency

    if (!effectiveCatalogue) return {
      availableSegments,
      availableProductTypesPerSegment,
      availableProductKindsPerSegment,
      hasSegments: false,
      catalogueCurrency,
      allProducts,
    }

    for (const segment of Object.values(effectiveCatalogue.segments)) {
      if (!segment?.key || !segment.productTypes) continue

      const productKindProductsMap: Partial<Record<ProductKind, ProductKindWithProducts>> = {}

      availableSegments.push(segment.key)

      for (const productTypeObject of Object.values(segment.productTypes)) {
        // Initialize values if undefined
        if (!availableProductTypesPerSegment[segment.key]) availableProductTypesPerSegment[segment.key] = {
          bundle: [],
          nonBundle: [],
          all: [],
        }
        if (!availableProductKindsPerSegment[segment.key]) availableProductKindsPerSegment[segment.key] = []

        const typeWithProducts = {
          productType: productTypeObject.key,
          products: productTypeObject.products
            .sort((productA, productB) => productA.price - productB.price)
            .map((product) => Object.assign(product, { type: productTypeObject.key }))
        }

        // Sort the kinds to bundled and nonBundled
        if (BundleProductTypes.has(productTypeObject.key)) availableProductTypesPerSegment[segment.key]!.bundle.push(typeWithProducts)
        else availableProductTypesPerSegment[segment.key]!.nonBundle.push(typeWithProducts)

        // Don't forget to push it to all collection
        availableProductTypesPerSegment[segment.key]!.all.push(typeWithProducts)

        // Group all products by kind and also push it to allProducts collection -- yes, yes, another for loop
        for (const product of productTypeObject.products) {
          const productWithType: CatalogueProduct = Object.assign(product, { type: productTypeObject.key })

          // Some kinds are surfaced as part of another product group (e.g. AI_PHOTO_EDITING is shown under PHOTO)
          const groupKind = resolveProductGroupKind(product.kind)

          productKindProductsMap[groupKind] = {
            productKind: groupKind,
            products: [...(productKindProductsMap[groupKind]?.products || []), productWithType]
          }

          allProducts.push(productWithType)
        }
      }

      // Transform grouped products into sorted array of { kind: ProductKind, products: Array<CatalogueProduct> } objects
      availableProductKindsPerSegment[segment.key] = Object.values(productKindProductsMap)
        .sort((kindGroupA, kindGroupB) => editingProductKindSortingOrder.indexOf(kindGroupA.productKind) - editingProductKindSortingOrder.indexOf(kindGroupB.productKind))

      // Sort them
      availableProductTypesPerSegment[segment.key]?.bundle.sort((typeA, typeB) => {
        if (typeA.productType < typeB.productType) return -1
        if (typeA.productType > typeB.productType) return 1
        return 0
      })
      availableProductTypesPerSegment[segment.key]?.nonBundle.sort((typeA, typeB) => {
        if (typeA.productType < typeB.productType) return -1
        if (typeA.productType > typeB.productType) return 1
        return 0
      })

    }

    // A segmentation-exempt category is served as one bucket, so the slider never
    // renders and the listing shows every product instead of only the band the client landed on.
    if (selectedCategory && isSegmentationExemptCategory(selectedCategory)) {
      const collapsed = collapseSegmentsToAll(availableProductTypesPerSegment, availableProductKindsPerSegment)

      return {
        ...collapsed,
        hasSegments: collapsed.availableSegments.length > 0,
        catalogueCurrency,
        allProducts,
      }
    }

    return {
      availableSegments: availableSegments.sort((segmentA, segmentB) => {
        if (segmentA < segmentB) return -1
        if (segmentA > segmentB) return 1
        return 0
      }),
      availableProductTypesPerSegment,
      availableProductKindsPerSegment,
      hasSegments: availableSegments.length > 0,
      catalogueCurrency,
      allProducts,
    }

  }, [effectiveCatalogue, selectedCategory])

  // INSTRUCTION CONFIG
  const getInstructionCatalogue = useGetInstructionConfig$()

  const computedInstructionData = useMemo(() => {
    const extractedData = getInstructionCatalogue.data?.data.data || null

    const allInstructionsByKind: Partial<Record<ProductKind, InstructionOptionDTO>> = {}
    const organizationInstructions: Partial<Record<ProductKind, InstructionOptionDTO>> = {}
    const availablePaymentMethods: Partial<Record<ProductKind, InstructionOptionDTO>> = {}
    // Extra Services can be only FAST_DELIVERY, CT_BOOKING options
    const extraServices: Partial<Record<ProductKind, InstructionOptionDTO>> = {}
    let externalReportingInstruction: Nullable<InstructionOptionDTO> = null

    for (const group of Object.values(extractedData?.instructionTypes || {})) {
      const instructionType = group.key

      for (const instruction of group.instructionOptions) {
        allInstructionsByKind[instruction.kind] = instruction


        if (instructionType === InstructionType.BILLING && instruction.kind === ProductKind.EXTERNAL_REPORTING) {
          externalReportingInstruction = instruction
          continue
        }

        if (instructionType === InstructionType.BILLING && instruction.isPrimary) {
          availablePaymentMethods[instruction.kind] = instruction
          continue
        }

        if (instructionType === InstructionType.ORGANIZATION) {
          organizationInstructions[instruction.kind] = instruction
          continue
        }

        extraServices[instruction.kind] = instruction
      }
    }

    // In case of adding any further conditions in InstructionListing file for SectionedBorderBox rendering, 
    // update also isInstructionStepSkipped
    const isInstructionStepSkipped = Object.values(organizationInstructions).length === 0
      && Object.values(extraServices).length === 0
      && (!selectedCategory || !isShootingCategory(selectedCategory))

    return {
      organizationInstructions,
      availablePaymentMethods,
      extraServices,
      externalReportingInstruction,
      catalogueTimezone: extractedData?.timezone,
      allInstructionsByKind,
      isInstructionStepSkipped,
    }
  }, [getInstructionCatalogue.data?.data.data, selectedCategory])

  // EXTRA products
  const getExtraProducts = useGetExtraProducts$()

  const extraProducts = useMemo(() => {
    const data = getExtraProducts.data?.data.data || []

    const serializedData: Record<string, CatalogueProduct[]> = {}

    for (const product of data) {
      serializedData[product.kind] = [
        ...(serializedData[product.kind] || []),
        product
      ]
    }

    return serializedData
  }, [getExtraProducts.data?.data.data])

  // Callback to get extra product details.
  // The extra product endpoint data is origin in CatalogueProduct. 
  // As a compromise, we need to convert it to CatalogueOptionProduct and manually set the value and duration for the extra products to be able to use it in the measurement page.
  const extraCatalogueOptionProduct = useCallback(
    (productKind: ProductKind) => toCatalogueOptionProduct(extraProducts[productKind]?.[0])
    , [extraProducts])

  const extraCatalogueProducts = useCallback((productKind: ProductKind) => {
    return extraProducts[productKind] ?? []
  }, [extraProducts])

  const toCatalogueOptionProduct = (catalogueProduct?: CatalogueProduct) => ({
    ...(catalogueProduct ?? {}),
    value: 0,
    duration: 0,
  } as unknown as CatalogueOptionProduct)

  // Invalidate on category/derived-address change (not the raw google place) so a late async geocode doesn't refetch an identical catalogue
  useEffect(
    () => {
      getProductCatalogue.reset()
      getExtraProducts.reset()
      setVatRateOverride(null)
      setDiscountOverride(null)
      // A category/address change genuinely invalidates the catalogue, so drop the snapshot too:
      // otherwise the embed fallback would keep rendering products priced for the previous context.
      lastSuccessfulCatalogueRef.current = null
    },

    // Exclude getProductCatalogue to prevent infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCategory, computedAddressData.selectedCity, computedAddressData.selectedCountryCode, computedAddressData.regionCode, computedAddressData.postalCode]
  )

  return {
    orderAttemptId,
    launchedFrom,
    getAddressConfig,
    getCategoryConfig,
    getProductCatalogue,
    getInstructionCatalogue,
    selectedCategory,
    selectedAssignmentPlace,
    getExtraProducts,
    extraProducts,
    enableSkipCtAutomation,
    enableSkipExternalProviderSending,
    setEnableSkipCtAutomation,
    setEnableSkipExternalProviderSending,
    extraCatalogueOptionProduct,
    extraCatalogueProducts,
    setSelectedCategory,
    setSelectedAssignmentPlace,
    setVatRateOverride,
    setDiscountOverride,
    ...computedCategoryData,
    ...computedAddressData,
    ...computedProductData,
    catalogueDiscount,
    catalogueVat,
    ...computedInstructionData,
    toCatalogueOptionProduct,
    isProductCatalogueLoading: getProductCatalogue.isPending || getProductCatalogue.isIdle,
    isRepricing,
  }
})
